import { useLocale } from "../context/LocaleContext";
import React from "react";
import { useNavigate } from "react-router-dom";
import Hero from "./Hero";
import DailyJourney from './DailyJourney';
import { mainCards, sectionCards, bottomCards } from "../mock";
import { Button } from "./ui/button";
import { ArrowRight } from "lucide-react";

function MainCard({ card, index }) {
  const { t } = useLocale();
  const navigate = useNavigate();
  return (
    <div
      className="sy-image-surface sy-card-glow sy-fade-in group relative flex min-h-[252px] cursor-pointer flex-col overflow-hidden rounded-2xl border"
      style={{
        borderColor: "var(--sy-border)",
        background: "var(--sy-panel)",
        animationDelay: `${index * 90}ms`,
      }}
      onClick={() => navigate(card.to)}
    >
      <div className="absolute inset-0">
        {card.image && <img
          src={card.image}
          alt=""
          className={`h-full w-full transition-transform duration-700 group-hover:scale-105 ${card.imgFit === "contain" ? "object-contain" : "object-cover"}`}
          style={{ objectPosition: card.imgPos || "center", opacity: card.imgOpacity ?? 0.55 }}
        />}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(8,20,17,0.55) 0%, rgba(8,20,17,0.82) 55%, rgba(8,20,17,0.96) 100%)",
          }}
        />
      </div>
      <div className="relative flex flex-1 flex-col p-5">
        <h3 className="font-display text-[22px] font-semibold leading-tight sy-gold-text">
          {t(card.title)}
        </h3>
        <p className="mt-2 max-w-md text-[13px] leading-relaxed" style={{ color: "var(--sy-text)" }}>
          {t(card.desc)}
        </p>
        <div className="mt-auto pt-4">
          <Button
            className="h-auto min-h-10 max-w-full whitespace-normal gap-2 px-3 py-2 text-xs font-semibold border"
            style={{
              background: "rgba(201,167,101,0.1)",
              borderColor: "rgba(201,167,101,0.45)",
              color: "var(--sy-gold-bright)",
            }}
            onClick={(e) => { e.stopPropagation(); navigate(card.to); }}
          >
            {t(card.cta)}
            <ArrowRight size={16} />
          </Button>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t pt-3" style={{ borderColor: "var(--sy-border)" }}>
          {card.stats.map((s, i) => {
            const Icon = s.icon;
            return (
              <span key={i} className="flex items-center gap-1.5 text-[11px]" style={{ color: "var(--sy-text-dim)" }}>
                <Icon size={14} style={{ color: "var(--sy-gold)" }} />
                {t(s.label)}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function SectionCard({ card, index }) {
  const { t } = useLocale();
  const navigate = useNavigate();
  const Icon = card.icon;
  return (
    <div
      className={`sy-image-surface sy-card-glow sy-fade-in group relative flex min-h-[158px] cursor-pointer flex-col overflow-hidden rounded-2xl border ${card.wide ? "lg:col-span-2" : ""}`}
      style={{ borderColor: "var(--sy-border)", background: "var(--sy-panel)", animationDelay: `${index * 70}ms` }}
      onClick={() => navigate(card.to)}
    >
      <div className="absolute inset-0">
        <img
          src={card.image}
          alt=""
          className="h-full w-full object-cover opacity-40 transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(8,20,17,0.6) 0%, rgba(8,20,17,0.94) 100%)" }} />
      </div>
      <div className="relative flex flex-1 flex-col p-4">
        <div
          className="mb-2.5 flex h-9 w-9 items-center justify-center rounded-xl border"
          style={{ borderColor: "rgba(201,167,101,0.35)", background: "rgba(201,167,101,0.1)" }}
        >
          <Icon size={18} style={{ color: "var(--sy-gold)" }} />
        </div>
        <h3 className="font-display text-[22px] font-semibold sy-gold-text">{t(card.title)}</h3>
        <p className="mt-1 text-[13px] leading-relaxed" style={{ color: "var(--sy-text-dim)" }}>
          {t(card.desc)}
        </p>
        <div className="mt-auto pt-3">
          <Button
            size="sm"
            className="gap-1.5 font-semibold border"
            style={{ background: "transparent", borderColor: "rgba(201,167,101,0.4)", color: "var(--sy-gold-bright)" }}
            onClick={(e) => { e.stopPropagation(); navigate(card.to); }}
          >
            {t(card.cta)}
            <ArrowRight size={14} />
          </Button>
        </div>
      </div>
    </div>
  );
}

function BottomCard({ card, index }) {
  const { t } = useLocale();
  const navigate = useNavigate();
  const Icon = card.icon;
  const isRed = card.accent === "red";
  return (
    <div
      className="sy-image-surface sy-card-glow sy-fade-in group relative flex cursor-pointer items-center gap-4 overflow-hidden rounded-2xl border p-4"
      style={{ borderColor: "var(--sy-border)", background: "var(--sy-panel)", animationDelay: `${index * 70}ms` }}
      onClick={() => navigate(card.to)}
    >
      <div className="absolute inset-0">
        <img src={card.image} alt="" className="h-full w-full object-cover opacity-30 transition-transform duration-700 group-hover:scale-105" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(8,20,17,0.95) 0%, rgba(8,20,17,0.8) 100%)" }} />
      </div>
      <div
        className={`relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border ${isRed ? "sy-ring-pulse" : ""}`}
        style={{
          borderColor: isRed ? "rgba(220,70,70,0.5)" : "rgba(201,167,101,0.35)",
          background: isRed ? "rgba(220,70,70,0.12)" : "rgba(201,167,101,0.1)",
        }}
      >
        <Icon size={20} style={{ color: isRed ? "#e4706e" : "var(--sy-gold)" }} />
      </div>
      <div className="relative min-w-0 flex-1">
        <h3 className="font-display text-lg font-semibold sy-gold-text">{t(card.title)}</h3>
        <p className="truncate text-[11px]" style={{ color: "var(--sy-text-dim)" }}>{t(card.desc)}</p>
      </div>
      <Button
        size="sm"
        className="relative shrink-0 gap-1.5 font-semibold"
        style={{
          background: isRed
            ? "linear-gradient(180deg,#c24a48 0%,#8f2f2e 100%)"
            : "linear-gradient(180deg,#e8d5a8 0%,#d6b876 55%,#b8954f 100%)",
          color: isRed ? "#fff" : "#1a150b",
        }}
        onClick={(e) => { e.stopPropagation(); navigate(card.to); }}
      >
        {t(card.cta)}
        <ArrowRight size={14} />
      </Button>
    </div>
  );
}

export default function HomePage() {
  const { t } = useLocale();
  return (
    <div className="pb-6">
      <Hero />

      <div className="px-6 lg:px-10">
        <DailyJourney />
        {/* Ana kartlar */}
        <div data-testid="main-library-cards" className="grid gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {mainCards.map((c, i) => (
            <MainCard key={c.id} card={c} index={i} />
          ))}
        </div>

        {/* Öne çıkan bölümler */}
        <h2 className="mt-7 mb-3 font-display text-[22px] font-semibold" style={{ color: "var(--sy-gold-bright)" }}>
          {t("Öne Çıkan Bölümler")}
        </h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {sectionCards.map((c, i) => (
            <SectionCard key={c.id} card={c} index={i} />
          ))}
        </div>

        {/* Alt kartlar */}
        <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {bottomCards.map((c, i) => (
            <BottomCard key={c.id} card={c} index={i} />
          ))}
        </div>
      </div>
    </div>
  );
}

